// Audio service for game sound effects
class AudioService {
  private audioContext: AudioContext | null = null
  private sounds: Map<string, HTMLAudioElement> = new Map()
  private isMuted: boolean = false
  private volume: number = 0.7 // UI volume (0-1), actual volume will be scaled to 0-0.2

  constructor() {
    this.initializeAudioContext()
    this.loadSounds()
    this.loadSettings()
  }

  private initializeAudioContext() {
    try {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
    } catch (error) {
      console.warn('AudioContext not supported:', error)
    }
  }

  private loadSounds() {
    const soundFiles = {
      'timer-warning': '/sounds/lasttensec.mp3',
      'prompt-lock': '/sounds/lock.mp3'
    }

    Object.entries(soundFiles).forEach(([key, path]) => {
      const audio = new Audio(path)
      audio.preload = 'auto'
      audio.volume = this.getActualVolume()
      this.sounds.set(key, audio)
    })
  }

  private loadSettings() {
    try {
      const savedMuted = localStorage.getItem('audio_muted')
      const savedVolume = localStorage.getItem('audio_volume')
      
      if (savedMuted !== null) {
        this.isMuted = savedMuted === 'true'
      }
      
      if (savedVolume !== null) {
        this.volume = parseFloat(savedVolume)
        this.updateAllVolumes()
      }
    } catch (error) {
      console.warn('Error loading audio settings:', error)
    }
  }

  private saveSettings() {
    try {
      localStorage.setItem('audio_muted', this.isMuted.toString())
      localStorage.setItem('audio_volume', this.volume.toString())
    } catch (error) {
      console.warn('Error saving audio settings:', error)
    }
  }

  private updateAllVolumes() {
    this.sounds.forEach(audio => {
      audio.volume = this.isMuted ? 0 : this.getActualVolume()
    })
  }

  // Public methods
  async playSound(soundKey: string): Promise<void> {
    if (this.isMuted) return

    try {
      // Resume AudioContext if suspended (required by some browsers)
      if (this.audioContext && this.audioContext.state === 'suspended') {
        await this.audioContext.resume()
      }

      const audio = this.sounds.get(soundKey)
      if (!audio) {
        console.warn(`Sound not found: ${soundKey}`)
        return
      }

      // Reset audio to beginning and play
      audio.currentTime = 0
      await audio.play()
    } catch (error) {
      console.warn(`Error playing sound ${soundKey}:`, error)
    }
  }

  setMuted(muted: boolean) {
    this.isMuted = muted
    this.updateAllVolumes()
    this.saveSettings()
  }

  setVolume(volume: number) {
    // Volume is stored as 0-1 but actual audio volume is scaled to 0-0.2
    this.volume = Math.max(0, Math.min(1, volume)) // Clamp between 0 and 1
    this.updateAllVolumes()
    this.saveSettings()
  }

  private getActualVolume(): number {
    // Scale volume from 0-1 to 0-0.2 (20% max volume)
    return this.volume * 0.2
  }

  getMuted(): boolean {
    return this.isMuted
  }

  getVolume(): number {
    return this.volume
  }

  // Specific sound methods for easy use
  async playTimerWarning(): Promise<void> {
    await this.playSound('timer-warning')
  }

  async playPromptLock(): Promise<void> {
    await this.playSound('prompt-lock')
  }

  // Test method for settings page
  async testSound(soundKey?: string): Promise<void> {
    const testKey = soundKey || 'prompt-lock'
    await this.playSound(testKey)
  }
}

// Create singleton instance
export const audioService = new AudioService()
export default audioService
